<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title class="mt-4">
          <v-btn
            class="mr-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon>
          </v-btn>
          <span> Edit Franchisee Details</span>
        </v-card-title>
        <page-loader :loading="isFetching" />
        <v-card-text>
          <v-row>
            <v-col sm="4">
              <v-text-field
                v-model="form.name"
                outlined
                label="Name *"
                prepend-inner-icon="mdi-buffet"
                :error-messages="errors.name"
              ></v-text-field>
            </v-col>
            <v-col sm="4">
              <v-text-field
                v-model="form.email"
                outlined
                label="Email *"
                prepend-inner-icon="mdi-email"
                :error-messages="errors.email"
              ></v-text-field>
            </v-col>
            <v-col sm="4">
              <v-text-field
                v-model="form.phone"
                outlined
                label="Phone *"
                prepend-inner-icon="mdi-phone"
                :error-messages="errors.phone"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-textarea
                outlined
                v-model="form.address"
                label="Address *"
                :error-messages="errors.address"
              ></v-textarea>
            </v-col>
            <v-col sm="6">
              <v-textarea
                v-model="form.description"
                outlined
                label="Description *"
                :error-messages="errors.description"
              ></v-textarea>
            </v-col>
            <v-col sm="4">
              <v-text-field
                v-model="form.latitude"
                outlined
                label="Latitude"
                prepend-inner-icon="mdi-buffet"
                :error-messages="errors.latitude"
              ></v-text-field>
            </v-col>
            <v-col sm="4">
              <v-text-field
                v-model="form.longitude"
                outlined
                label="Longitude"
                prepend-inner-icon="mdi-buffet"
                :error-messages="errors.longitude"
              ></v-text-field>
            </v-col>
            <v-col sm="4">
              <v-text-field
                v-model="form.pincode"
                outlined
                label="Pincode"
                prepend-inner-icon="mdi-map-marker"
                :error-messages="errors.pincode"
              ></v-text-field>
            </v-col>
            <v-col sm="4">
              <v-switch
                class="mt-0 ml-5"
                v-model="form.is_active"
                inset
                :label="`Is Active: ${form.is_active ? 'YES' : 'NO'}`"
              ></v-switch>
            </v-col>
            <v-col sm="4">
              <v-menu
                ref="start_time_menu"
                v-model="form.start_time_menu"
                :close-on-content-click="false"
                :nudge-right="40"
                :return-value.sync="form.start_time"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.start_time"
                    label="Working from"
                    outlined
                    prepend-inner-icon="mdi-clock-time-four-outline"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    clearable
                    @click:clear="form.start_time = null"
                    :error-messages="errors.working_hours"
                  ></v-text-field>
                </template>
                <v-time-picker
                  v-if="form.start_time_menu"
                  v-model="form.start_time"
                  full-width
                  :allowed-minutes="allowedStep"
                  @click:minute="$refs.start_time_menu.save(form.start_time)"
                ></v-time-picker>
              </v-menu>
            </v-col>
            <v-col sm="4">
              <v-menu
                ref="end_time_menu"
                :close-on-content-click="false"
                v-model="form.end_time_menu"
                :nudge-right="40"
                :return-value.sync="form.end_time"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.end_time"
                    label="Working Till"
                    outlined
                    prepend-inner-icon="mdi-clock-time-four-outline"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    clearable
                    @click:clear="form.end_time = null"
                    :error-messages="errors.working_hours"
                  ></v-text-field>
                </template>
                <v-time-picker
                  v-model="form.end_time"
                  full-width
                  :min="form.start_time"
                  @click:minute="$refs.end_time_menu.save(form.end_time)"
                  :allowed-minutes="allowedStep"
                ></v-time-picker>
              </v-menu>
            </v-col>

            <template v-if="visible_to(['ADMIN', 'SUPER ADMIN'])">
              <v-col sm="4">
                <v-switch
                  class="mt-0 ml-5"
                  v-model="form.is_kyc_valid"
                  inset
                  :label="`Is KYC Done: ${form.is_kyc_valid ? 'YES' : 'NO'}`"
                ></v-switch>
              </v-col>
              <v-col sm="4">
                <v-autocomplete
                  v-model="form.status"
                  outlined
                  :items="statusItems"
                  label="Status"
                  prepend-inner-icon="mdi-checkbox-marked-circle-outline"
                ></v-autocomplete>
              </v-col>
              <v-col sm="4" v-if="form.status == 2">
                <v-text-field
                  v-model="form.rejection_reason"
                  outlined
                  label="Remark"
                  prepend-inner-icon="mdi-card-text-outline"
                ></v-text-field>
              </v-col>
            </template>
          </v-row>
          <bank-details-fields :form="form" />

          <v-divider class="mt-5 mb-5"> </v-divider>
          <v-btn v-can:edit="['franchisees', 'franchise_details']"
            :loading="isLoading"
            :disabled="isLoading"
            color="primary"
            rounded
            @click="save"
          >
            Update
          </v-btn>
        </v-card-text>
      </v-card>

      <v-card elevation="6" rounded="xxl" class="mt-4">
        <v-card-title>
          <span>Time Slots</span>
          <v-spacer></v-spacer>
          <v-btn v-can:add="['franchisees', 'franchise_details']" color="primary" rounded @click="openSlotDialog()">
            <v-icon left>mdi-plus</v-icon>
            Add Slot
          </v-btn>
        </v-card-title>
        <v-card-text>
          <p class="text-caption grey--text" v-if="timeSlots.length">
            30-min slots are auto-generated from Working from/Working Till.
            Use the toggle to disable a slot.
          </p>
          <v-simple-table>
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">Sr No</th>
                  <th class="text-left">Start Time</th>
                  <th class="text-left">End Time</th>
                  <th class="text-left">Status</th>
                  <th class="text-left">Actions</th>
                </tr>
              </thead>
              <tbody v-if="timeSlots.length != 0">
                <tr
                  v-for="(slot, slotIndex) in timeSlots"
                  :key="`slot_${slot.id}`"
                >
                  <td>{{ slotIndex + 1 }}</td>
                  <td>{{ slot.start_time }}</td>
                  <td>{{ slot.end_time }}</td>
                  <td>
                    <v-chip
                      small
                      :color="slot.is_active ? 'green' : 'grey'"
                      text-color="white"
                    >
                      {{ slot.is_active ? "Active" : "Disabled" }}
                    </v-chip>
                  </td>
                  <td>
                    <!-- <v-btn icon small @click="openSlotDialog(slot)">
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn> -->
                    <v-switch
                      class="d-inline-block mb-0"
                      hide-details
                      dense
                      inset
                      :input-value="slot.is_active"
                      @change="toggleSlotActive(slot)"
                    ></v-switch>
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td colspan="5" class="text-center">
                    No time slots yet. Set "Working from"/"Working Till" and
                    save, then reopen this page.
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
      </v-card>

      <v-dialog v-model="slotDialog" max-width="500" persistent>
        <v-card>
          <v-card-title>{{ slotForm.id ? "Edit" : "Add" }} Time Slot</v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="6">
                <v-menu
                  ref="slot_start_menu"
                  v-model="slotStartMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="slotForm.start_time"
                      label="Start Time"
                      outlined
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      :error-messages="slotErrors.start_time"
                    ></v-text-field>
                  </template>
                  <v-time-picker
                    v-if="slotStartMenu"
                    v-model="slotForm.start_time"
                    full-width
                    :allowed-minutes="allowedStep"
                    @click:minute="$refs.slot_start_menu.save(slotForm.start_time)"
                  ></v-time-picker>
                </v-menu>
              </v-col>
              <v-col cols="6">
                <v-menu
                  ref="slot_end_menu"
                  v-model="slotEndMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="slotForm.end_time"
                      label="End Time"
                      outlined
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      :error-messages="slotErrors.end_time"
                    ></v-text-field>
                  </template>
                  <v-time-picker
                    v-if="slotEndMenu"
                    v-model="slotForm.end_time"
                    full-width
                    :min="slotForm.start_time"
                    :allowed-minutes="allowedStep"
                    @click:minute="$refs.slot_end_menu.save(slotForm.end_time)"
                  ></v-time-picker>
                </v-menu>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="slotDialog = false">Cancel</v-btn>
            <v-btn v-can:edit="['franchisees', 'franchise_details']"
              color="primary"
              :loading="isSlotSaving"
              :disabled="isSlotSaving"
              @click="saveSlot"
              >Save</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>
<script>
import axios from "axios";
export default {
  name: "FranchiseeEditPage",
  data() {
    return {
      form: {
        start_time_menu: false,
        end_time_menu: false,
      },
      allowedMins: ["00", "30"],
      isLoading: false,
      isFetching: false,
      statusItems: [
        { id: "PENDING", text: "PENDING", value: 0 },
        { id: "APPROVED", text: "APPROVED", value: 1 },
        { id: "REJECTED", text: "REJECTED", value: 2 },
      ],
      timeSlots: [],
      slotDialog: false,
      slotStartMenu: false,
      slotEndMenu: false,
      isSlotSaving: false,
      slotForm: {},
      slotErrors: {},
    };
  },
  async mounted() {
    this.isFetching = true;
    try {
      await this.getData();
    } finally {
      this.isFetching = false;
    }
  },
  methods: {
    allowedStep: (m) => m % 30 === 0,
    async getData() {
      this.isLoading = true;
      let encryptedId = this.$route.params.franchiseeId;
      axios
        .get(`/franchisees/${encryptedId}`)
        .then((response) => {
          this.form = response.data.data;
          console.log(this.form);
          this.old_json = response.data.data;
          this.getTimeSlots();
        })
        .catch((error) => {
          this.isLoading = false;
          if (error.response.status != 422) {
            console.error(error);
            this.isUnAuthorised();
          }
        });
      this.isLoading = false;
    },
    async getTimeSlots() {
      await axios
        .get("/franchisee_time_slots", {
          params: { franchisee_id: this.form.id },
        })
        .then((response) => {
          this.timeSlots = response.data.data;
        })
        .catch((error) => {
          console.error(error);
        });
    },
    openSlotDialog(slot) {
      this.slotErrors = {};
      this.slotForm = slot
        ? { id: slot.id, start_time: slot.start_time, end_time: slot.end_time }
        : { start_time: null, end_time: null };
      this.slotDialog = true;
    },
    async saveSlot() {
      this.slotErrors = {};
      if (!this.slotForm.start_time || !this.slotForm.end_time) {
        this.slotErrors = {
          start_time: !this.slotForm.start_time ? ["Required"] : [],
          end_time: !this.slotForm.end_time ? ["Required"] : [],
        };
        return;
      }
      this.isSlotSaving = true;
      try {
        if (this.slotForm.id) {
          await axios.patch(
            `/franchisee_time_slots/${this.encryptIt(this.slotForm.id)}`,
            this.slotForm
          );
        } else {
          await axios.post("/franchisee_time_slots", {
            franchisee_id: this.form.id,
            start_time: this.slotForm.start_time,
            end_time: this.slotForm.end_time,
          });
        }
        this.slotDialog = false;
        await this.getTimeSlots();
      } catch (error) {
        if (error.response && error.response.status === 422) {
          this.slotErrors = error.response.data.errors || {};
          if (error.response.data["sub-title"]) {
            this.slotErrors.start_time = [error.response.data["sub-title"]];
          }
        } else {
          console.error(error);
        }
      } finally {
        this.isSlotSaving = false;
      }
    },
    async toggleSlotActive(slot) {
      try {
        await axios.patch(`/franchisee_time_slots/${this.encryptIt(slot.id)}`, {
          start_time: slot.start_time,
          end_time: slot.end_time,
          is_active: !slot.is_active,
        });
        await this.getTimeSlots();
      } catch (error) {
        console.error(error);
      }
    },
    async save() {
      try {
        this.isLoading = true;
        this.form.is_kyc_valid = this.form.is_kyc_valid ? 1 : 0;
        await axios
          .patch(`/franchisees/${this.$route.params.franchiseeId}`, this.form)
          .then(async () => {
            await this.saveUserTimestamp(this.old_json, this.form);
            this.$router.go(-1);
          })
          .catch((error) => {
            this.isLoading = false;
            if (error.response.status != 422) {
              console.error(error);
              // this.isUnAuthorised();
            }
          });
        this.isLoading = false;
      } catch (e) {
        this.isLoading = false;
      }
    },
  },
};
</script>
